import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subscription } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { Shipment } from 'src/app/loads-shared/shipment-data/models/shipment';
import { RegistrationService } from 'src/app/registration/shared/registration.service';
import { DEFAULT_ERROR_MESSAGE, LOAD_SIZE_FTL } from '../shared/constants';
import { DriverApiService } from 'src/app/shared/services/driver-api.service';
import { ShipmentsDataService } from '../shared/shipments-data.service';
import { ToastService } from 'src/app/shared/toast/toast.service';
import { ShipmentsUtilsService } from '../shared/shipments-utils.service';
import dayjs from 'dayjs';
import { GeneralSearchService } from 'src/app/shared/services/general-search/general-search.service';
import { SHIPMENTS_ROUTE, SHIPMENTS_ROUTE_FILTER_SHIPMENTS } from '../shared/routes';
import { ShipmentsItemComponent } from '../shipments-item/shipments-item.component';
import { NoShipmentComponent } from 'src/app/loads-shared/no-shipment/no-shipment.component';

const MM_DD_YYYY = 'MM/DD/YYYY';

@Component({
    selector: 'app-shipments-history',
    templateUrl: './shipments-history.component.html',
    styleUrls: ['./shipments-history.component.scss'],
    standalone: true,
    imports: [
      CommonModule,
      MatButtonModule,
      MatIconModule,
      MatInputModule,
      MatFormFieldModule,
      MatProgressSpinnerModule,
      ShipmentsItemComponent,
      NoShipmentComponent
    ]
})
export class ShipmentsHistoryComponent implements OnInit, OnDestroy {
  private subscription: Subscription;
  private shipments: Shipment[];
  loading: boolean;
  driverId: any;
  sortOrderAsc: boolean = true;
  currentSearchInput: string;
  filterFormState: any;
  someTruthy: boolean;
  allShipments: Shipment[];
  noShipmentHistory = 'Your shipment history list is empty';
  tagsList: string[];

  constructor(
    private readonly registrationService: RegistrationService,
    private readonly driverApiService: DriverApiService,
    private readonly shipmentsDataService: ShipmentsDataService,
    private readonly toastService: ToastService,
    private readonly shipmentsUtilsService: ShipmentsUtilsService,
    private readonly generalSearchService: GeneralSearchService,
    @Inject('environmentData') public environment: any,
    private readonly router: Router,
    private readonly location: Location
  ) {
    this.subscription = new Subscription();
    this.loading = false;
  }

  ngOnInit(): void {
    this.driverId = this.driverApiService.driverID;
    this.getShipmentsHistory();
    this.currentSearchInput = '';
    this.filterFormState = window.history.state?.filter || {};
    this.someTruthy = Object.values(this.filterFormState).some(val => val);
    this.setTagsList();
  }

  navigateBack(): void {
    this.registrationService.navigateToProfile();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  getShipmentsHistory(): void {
    this.loading = true;
    this.shipments = [];
    this.subscription.add(
      this.shipmentsDataService
        .getShipmentsHistory(this.driverId)
        .pipe(
          catchError((error: any) => {
            this.toastService.showError(error?.message || DEFAULT_ERROR_MESSAGE);
            return error;
          }),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe((shipments: Shipment[]) => {
          if (shipments?.length) {
            this.shipments = shipments.map(shipment => ({
              ...shipment,
              status: 'delivered',
              shipmentStatus: 'delivered'
            }));;
            this.shipments = this.shipmentsUtilsService.sortShipmentsPickupAsc(this.shipments, this.sortOrderAsc);
            this.allShipments = this.shipments;
            this.filteredUpcomigShipments(this.allShipments)
          }
        })
    );
  }

  onSearchInput(search: string): void {
    this.currentSearchInput = search?.trim();
    this.filteredUpcomigShipments([...this.allShipments])
  }

  filteredUpcomigShipments(shipments: Shipment[]) {
    let filteredShipments: Shipment[] = shipments;
    if (this.someTruthy) {
      filteredShipments = shipments.filter(
        el =>
          this.isPickUpInBetweenFilterDates(el) &&
          this.isFilterFieldPresentinShipment(el, 'status', 'status') &&
          this.isFilterFieldPresentinShipment(el, 'loadSize', 'loadSize') &&
          this.isFilterFieldPresentinShipment(el, 'origin', 'pickUpCity') &&
          this.isFilterFieldPresentinShipment(el, 'destination', 'dropOffCity') &&
          this.isFilterFieldPresentinShipment(el, 'unitNumber', 'unitNumber') &&
          this.isFilterFieldPresentinShipment(el, 'loadType', 'loadType') &&
          this.isFilterFieldPresentinShipment(el, 'trailerNumber', 'trailerNumber')
      );
    }
    this.searchShipmentsResults(filteredShipments);
  }

  isFilterFieldPresentinShipment(el: Shipment, shipmentField: string, filterField: string): boolean {
    let formField: string = this.filterFormState[filterField]?.toLowerCase();
    return formField ? el[shipmentField]?.toLowerCase().includes(formField || '') : true;
  }

  isPickUpInBetweenFilterDates(el: Shipment): boolean {
    let pickupDateFrom = dayjs(el.pickup.pickupDateFrom).format(MM_DD_YYYY),
      filterFromDate = this.filterFormState.fromDate,
      filterToDate = this.filterFormState.toDate;
    let isBetween = false;

    if (filterFromDate && filterToDate) {
      isBetween =
        new Date(pickupDateFrom) >= new Date(filterFromDate) && new Date(pickupDateFrom) <= new Date(filterToDate);
    } else if (
      (filterFromDate && new Date(pickupDateFrom) >= new Date(filterFromDate)) ||
      (filterToDate && new Date(pickupDateFrom) <= new Date(filterToDate)) ||
      (!filterFromDate && !filterToDate)
    ) {
      isBetween = true;
    }
    return isBetween;
  }

  navigateToFilterShipments() {
    this.router.navigate([`/${SHIPMENTS_ROUTE}/${SHIPMENTS_ROUTE_FILTER_SHIPMENTS}`], {
      state: { 
        filter: window.history.state?.filter,
        pageType: 'history'
       }
    });
  }

  sortByStatus(): void {
    this.sortOrderAsc = !this.sortOrderAsc;
    this.shipments = this.shipmentsUtilsService.sortShipmentsPickupAsc(this.shipments, this.sortOrderAsc);
  }

  setTagsList() {
    let tags: string[] = [];
    let filterKeys = Object.entries(this.filterFormState)
      .filter(([key, value]) => value)
      .map(el => {
        return el[0];
      });
    // from - to date tag
    if (this.filterFormState.fromDate && this.filterFormState.toDate) {
      tags.push(`${this.filterFormState.fromDate} - ${this.filterFormState.toDate}`);
      filterKeys = filterKeys.filter(el => el !== 'fromDate' && el !== 'toDate');
    }
    // pickup - drop tag
    if (this.filterFormState.pickUpCity && this.filterFormState.dropOffCity) {
      tags.push(`${this.filterFormState.pickUpCity} - ${this.filterFormState.dropOffCity}`);
      filterKeys = filterKeys.filter(el => el !== 'pickUpCity' && el !== 'dropOffCity');
    }

    filterKeys.forEach((el: any) => {
      if (
        [
          'pickUpCity',
          'dropOffCity',
          'status',
          'loadSize',
          'fromDate',
          'toDate',
          'unitNumber',
          'loadType',
          'trailerNumber'
        ].includes(el)
      ) {
        tags.push(this.filterFormState[el]);
      }
    });

    this.tagsList = tags;
  }

  private searchShipmentsResults(filterShipments: Shipment[]): void {
    if (!this.currentSearchInput) {
      this.shipments = [...filterShipments];
      return;
    }

    this.shipments = filterShipments.filter(el => {
      let requiredSearchFields: any = {
        shipmentID: el.shipmentID,
        pickupDateFrom: dayjs(el.pickup.pickupDateFrom).format('MMM DD YYYY, hh:mma'),
        pickupDateTo: dayjs(el.pickup.pickupDateTo).format('MMM DD YYYY, hh:mma'),

        origin: el.origin,
        destination: el.destination,
        status: el.status,
        offerRate: el.offerRate,
        distance: el.distance,
        loadSize: el.loadSize,
        weight: el.weight,
        deadHeadOrigin: el.deadHeadOrigin,
        deadHeadDestination: el.deadHeadDestination,
        unitNumber: el.unitNumber,
        loadType: el.loadType,
        trailerNumber: el.trailerNumber
      };
      if (Array.isArray(el.delivery)) {
        const deliveryObj = this.shipmentsUtilsService.getDeliveryDate(el.delivery);
        requiredSearchFields.deliveryDateFrom = dayjs(deliveryObj?.deliveryDateFrom).format('MMM DD YYYY, hh:mma');
        requiredSearchFields.deliveryDateTo = dayjs(deliveryObj?.deliveryDateTo).format('MMM DD YYYY, hh:mma');
      } else {
        requiredSearchFields.deliveryDateFrom = dayjs(el.delivery?.deliveryDateFrom).format('MMM DD YYYY, hh:mma');
        requiredSearchFields.deliveryDateTo = dayjs(el.delivery?.deliveryDateTo).format('MMM DD YYYY, hh:mma');
      }

      if (this.environment?.featureFlags?.enableSearchByIDonShipments) {
        requiredSearchFields.id = el.id;
      }

      if (this.generalSearchService.searchField(requiredSearchFields, this.currentSearchInput)) {
        return el;
      }
    });
  }
}
